import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { PlatformUserService } from './platform-user.service';
import { LocalService } from './local.service';
import { map, switchMap, catchError, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService {

  path: import("@angular/router").ActivatedRouteSnapshot[];
  route: import("@angular/router").ActivatedRouteSnapshot;

  private currentUserProfile: any = null;

  constructor(
    private router: Router,
    public af: Auth,
    private platformUserService: PlatformUserService,
    private localService: LocalService
  ) {
    // Invalidate cache on logout
    authState(this.af).subscribe(user => {
      if (!user) {
        this.currentUserProfile = null;
      }
    });
  }

  // Get Current Authenticated User
  getUser(): Promise<any> {
    return firstValueFrom(authState(this.af));
  }

  // Guard Logic implementation
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {

    // Check Cache First
    if (this.currentUserProfile) {
      return this.checkUserRole(this.currentUserProfile, state.url);
    }

    return from(this.getUser()).pipe(
      switchMap((firebaseUser) => {
        if (!firebaseUser) {
          this.router.navigate(['login'], { queryParams: { returnUrl: state.url } });
          return of(false);
        }

        const userKey = this.localService.getJsonValue('UserKey');
        let userObservable: Observable<any>;

        if (userKey) {
          userObservable = this.platformUserService.fetchPlatformUser(userKey);
        } else {
          userObservable = this.platformUserService.fetchPlatformUserByUid(firebaseUser.uid);
        }

        return userObservable.pipe(
          tap(user => {
            if (user) {
              this.currentUserProfile = user;
            }
          }),
          map((user) => {
            if (user) {
              return this.checkUserRole(user, state.url);
            }
            return true;
          }),
          catchError(() => {
            return of(true);
          })
        );
      })
    );
  }

  private checkUserRole(user: any, url: string): boolean | UrlTree {
    if (user.role === 'Owner') {
      if (url.startsWith('/owners')) {
        return true;
      } else {
        return this.router.createUrlTree(['/owners/dashboard']);
      }
    }
    // Non-owner user
    return true;
  }
}
